import { AccountType } from "../shared/types/accounts";
import axios from "axios";
import { Store as store } from "../store";
import { toast } from "react-toastify";
import { trackEventWithUser } from "../libs/analytics";
import { uploadFileToS3 } from "../api/media-library/upload-file-to-s3";
import {
  getAccounts as getAccountsSelector,
  getSelectedAccountIds,
} from "../selectors/skedCoreSelectors";
import type { User } from "../features/user/types";
import {
  type ScheduleSubmitScope,
  withScheduleIdempotencyKey,
} from "../api/schedule-idempotency";

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Legacy Services Module
 *
 * This module provides service implementations that were originally part of an
 * Angular application. These services are now implemented using React/Redux
 * state and axios for HTTP calls.
 *
 * These services are being gradually migrated to React Query hooks and Redux
 * actions. See ANGULAR_MIGRATION_CHECKLIST.md for migration progress.
 *
 * @deprecated Import individual services or use React Query hooks instead
 */

// Get user from Redux store
function getUser(): User {
  const state = store.getState();
  return (state?.user ?? {}) as User;
}

// Get accounts from Redux store using centralized selector
function getAccounts(): AccountType[] {
  // Cast to any to avoid type mismatch between store state and ReduxRootState
  // This legacy service will be deprecated in favor of hooks
  const state = store.getState() as any;
  return getAccountsSelector(state);
}

function getAccountsMap(): Record<string, AccountType> {
  const accounts = getAccounts();
  return accounts.reduce(
    (map, account) => {
      map[account._id] = account;
      return map;
    },
    {} as Record<string, AccountType>
  );
}

function idempotencyHeaders(idempotencyKey: string | undefined) {
  return idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {};
}

// Schedule Service - implements post scheduling operations via API
export const scheduleService: IScheduleService = {
  async findPosts(params: any) {
    const response = await axios.post("/posts", params);
    return response.data;
  },

  findCalendarPosts(params: any) {
    return axios.post("/calendar/posts", params).then((r) => r.data);
  },

  async findLatestPosts(params: any) {
    const response = await axios.get("/posts/latest", { params });
    return response.data;
  },

  async findArchives(params: any) {
    const response = await axios.get("/posts/archives", {
      params: { query: encodeURIComponent(JSON.stringify(params)) },
    });
    return response.data;
  },

  async findActive(page: number, pageSize: number, accountIds: any[]) {
    const response = await axios.post("/schedules/active", {
      page: Math.max(1, page || 1),
      pageSize,
      "accountIds[]": accountIds,
    });
    return response.data;
  },

  async findArchived(
    page: number,
    pageSize: number,
    accountIds: any[],
    params?: any
  ) {
    const response = await axios.post("/schedules/inactive", {
      page: Math.max(1, page || 1),
      pageSize,
      "accountIds[]": accountIds,
      ...params,
    });
    return response.data;
  },

  async findDrafts(
    page: number,
    pageSize: number,
    accountIds: any[],
    params?: any
  ) {
    const response = await axios.post("/schedules/drafts", {
      page: Math.max(1, page || 1),
      pageSize,
      "accountIds[]": accountIds,
      ...params,
    });
    return response.data;
  },

  async searchDrafts(accountIds: any[], searchText: string) {
    const response = await axios.get("/drafts/search", {
      params: { accountIds, searchText },
    });
    return response.data;
  },

  async findActiveByTime(accountIds: any[]) {
    const response = await axios.get("/schedules/active/by-time", {
      params: { "accountIds[]": accountIds },
    });
    return response.data;
  },

  makeSchedule(post: any) {
    // Transform post data to schedule format
    return {
      ...post,
      schedule: post.when ? { when: post.when } : undefined,
    };
  },

  normaliseComments(post: any) {
    if (!post.comments) return post;
    return {
      ...post,
      comments: Array.isArray(post.comments) ? post.comments : [post.comments],
    };
  },

  normaliseTags(tags: any): any[] {
    if (!tags) return [];
    return Array.isArray(tags) ? tags : [tags];
  },

  async update(id: string, schedule: any) {
    const response = await axios.put(`/schedules/${id}`, schedule);
    return response.data;
  },

  async savePost(post: any) {
    if (post._id && !post.newPost) {
      const response = await axios.put(`/schedules/${post._id}`, post);
      return response.data;
    } else {
      const response = await axios.post("/schedules", post);
      return response.data;
    }
  },

  async clearSchedule(accountId: string) {
    const response = await axios.delete(`/insta-schedule/${accountId}/clear`);
    return response.data;
  },

  async copySchedule(sourceId: string, destinationId: string) {
    const response = await axios.post(
      `/insta-schedule/${sourceId}/copyTo/${destinationId}`
    );
    return response.data;
  },

  async moveToQueue(post: any) {
    const response = await axios.put(`/schedules/${post._id}/queue`, post);
    return response.data;
  },

  async moveToSchedule(post: any) {
    const response = await axios.put(`/queue/${post._id}/schedule`, post);
    return response.data;
  },

  async updatePost(post: any) {
    const resource = post.queued ? "/queue/" : "/schedules/";
    const response = await axios.put(`${resource}${post._id}`, post);
    return response.data;
  },

  async updateRedirectUrl(post: any) {
    const response = await axios.put(
      `/schedules/${post._id}/updateRedirectUrl`,
      {
        url: post.redirectUrl,
      }
    );
    return response.data;
  },

  async updateCalendarSchedule(
    id: string,
    schedule: any,
    hideCalendarDialog: boolean
  ) {
    const response = await axios.put(`/calendar/${id}`, {
      schedule,
      hideCalendarDialog,
    });
    return response.data;
  },

  async moveCalendarPost(id: string, post: any, data: any) {
    if (post.queued) {
      const response = await axios.post(`/queue/${id}/schedule`, data);
      return response.data;
    }
    return this.update(id, post);
  },

  schedulesOverlap(schedules: any[]): [number, number] | undefined {
    // Check for overlapping schedules
    for (let i = 0; i < schedules.length; i++) {
      for (let j = i + 1; j < schedules.length; j++) {
        if (schedules[i].when === schedules[j].when) {
          return [i, j];
        }
      }
    }
    return undefined;
  },

  async create(
    posts: any,
    uploader: any,
    suppress?: boolean,
    scope?: ScheduleSubmitScope
  ) {
    const body = { ...posts, uploader, suppress };
    const isDraftSave =
      Array.isArray(posts.schedules) &&
      posts.schedules.length > 0 &&
      posts.schedules.every(
        (schedule: any) => schedule?.actionType === "draft"
      );
    return await withScheduleIdempotencyKey(
      scope,
      body,
      (idempotencyKey) =>
        axios.post("/schedules", body, {
          headers: idempotencyHeaders(idempotencyKey),
        }),
      isDraftSave ? "draft_save" : "schedule"
    );
  },

  async cancel(id: string) {
    const response = await axios.delete(`/schedules/active/${id}`);
    return response.data;
  },

  async findById(id: string) {
    const response = await axios.get(`/schedules/${id}`);
    return response.data;
  },

  async remove(id: string) {
    const response = await axios.delete(`/schedules/inactive/${id}`);
    return response.data;
  },

  async removeFromQueue(id: string) {
    const response = await axios.delete(`/schedules/${id}`);
    return response.data;
  },

  async moveToDraft(id: string, queued: boolean) {
    const endpoint = queued ? `/queue/${id}/draft` : `/schedules/${id}/draft`;
    const response = await axios.post(endpoint);
    return response.data;
  },
};

// Post Service - implements post-related utilities
export const postService: IPostService = {
  getNext15Min() {
    const now = new Date();
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now;
  },

  draft: {
    async update(post: any) {
      const response = await axios.put(`/drafts/${post._id}`, post);
      return response.data;
    },
    async queue(post: any) {
      const response = await axios.post(`/drafts/${post._id}/queue`, post);
      return response.data;
    },
    async schedule(post: any, scope?: ScheduleSubmitScope) {
      const response = await withScheduleIdempotencyKey(
        scope,
        post,
        (idempotencyKey) =>
          axios.post(`/drafts/${post._id}/schedule`, post, {
            headers: idempotencyHeaders(idempotencyKey),
          })
      );
      return response.data;
    },
  },

  queue: {
    async update(post: any) {
      const response = await axios.put(`/queue/${post._id}`, post);
      return response.data;
    },
    async draft(post: any) {
      const response = await axios.post(`/queue/${post._id}/draft`, post);
      return response.data;
    },
    async schedule(post: any) {
      const response = await axios.post(`/queue/${post._id}/schedule`, post);
      return response.data;
    },
  },

  schedule: {
    async update(post: any) {
      const response = await axios.put(`/schedules/${post._id}`, post);
      return response.data;
    },
    async draft(post: any) {
      const response = await axios.post(`/schedules/${post._id}/draft`, post);
      return response.data;
    },
    async queue(post: any) {
      const response = await axios.post(`/schedules/${post._id}/queue`, post);
      return response.data;
    },
  },
};

// Posts Service - implements filter creation and other utilities
export const postsService: IPostsService = {
  makeFilters(accountIds: string[], limit?: number) {
    // Convert accountIds array to accountFilters object format expected by backend
    const accountFilters = accountIds.reduce(
      (acc, accountId) => {
        acc[accountId] = { lastOrdinal: 0 };
        return acc;
      },
      {} as Record<string, { lastOrdinal: number }>
    );

    return {
      accountFilters,
      lastNextRunAt: 0,
      limit: limit || 50,
      offset: 0,
    };
  },
};

// Collaboration Service - implements collaboration status updates
export const collaborationService: ICollaborationService = {
  async updatePostStatusKey(postIds: string[], postType: string, key: string) {
    await axios.put("/collaboration/status", {
      postIds,
      postType,
      statusKey: key,
    });
  },
};

// Posts Controller - compatibility layer for controller methods
export const postsController: IPostsController = {
  currentPostUrl: undefined,
  post: undefined,

  getSelectedAccounts() {
    // Use centralized selector for consistency with rest of codebase
    // Cast to any to avoid type mismatch between store state and ReduxRootState
    const state = store.getState() as any;
    return getSelectedAccountIds(state);
  },

  toggleCollaborateWidget(index: number, post: any) {
    console.warn(
      "toggleCollaborateWidget called - implement React equivalent",
      index,
      post
    );
  },

  closeModal() {
    // Dispatch close modal action if needed
    console.warn("postsController.closeModal called - use React modal state");
  },
};

// Storage Service - uses localStorage
export const storage: IStorageService = {
  getObject(key: string) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },

  putObject(key: string, value: any) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error("Failed to save to localStorage", e);
    }
  },

  remove(key: string) {
    localStorage.removeItem(key);
  },
};

// Modal Service - compatibility layer
export const modalService: IModalService = {
  open(config: any) {
    console.warn("modalService.open called - use React modal instead", config);
    return {
      result: Promise.resolve(),
      close: () => {},
      dismiss: () => {},
    };
  },

  close() {
    console.warn("modalService.close called - use React modal state");
  },

  createPostModal(config: any, callback?: () => void) {
    console.warn(
      "modalService.createPostModal called - use React modal instead",
      config
    );
    callback?.();
  },
};

// HTTP client wrapper (prefer using axios directly)
export const httpClient = {
  get: (url: string, config?: any) =>
    axios.get(url, config).then((r) => ({ data: r.data })),
  post: (url: string, data?: any, config?: any) =>
    axios.post(url, data, config).then((r) => ({ data: r.data })),
  put: (url: string, data?: any, config?: any) =>
    axios.put(url, data, config).then((r) => ({ data: r.data })),
  delete: (url: string, config?: any) =>
    axios.delete(url, config).then((r) => ({ data: r.data })),
};

// Toaster service (prefer using react-toastify directly)
export const toaster = {
  success: (opts: { title: string }) => {
    toast.success(opts.title);
  },
  error: (opts: { title: string }) => {
    toast.error(opts.title);
  },
  clear: () => {
    toast.dismiss();
  },
};

// Loading spinner service - uses Redux loading state
// Dispatches actions to toggle global loading state via loadingReducer
// The reducer uses ROUTINE_NAME/REQUEST to start and ROUTINE_NAME/SUCCESS to stop
export const loadingSpinnerService = {
  showLoading: () => {
    store.dispatch({ type: "LEGACY_SPINNER/REQUEST" });
  },
  hideLoading: () => {
    store.dispatch({ type: "LEGACY_SPINNER/SUCCESS" });
  },
};

// Event tracking service (use analytics module instead)
type LegacyTrackEvent = {
  eventName: string;
  metadata?: Record<string, any>;
  useServices?: Array<"AMP" | "HS">;
  user?: ReturnType<typeof getUser>;
};

export const eventService = {
  trackEvent: (event: LegacyTrackEvent) => {
    const user = event.user || getUser();
    trackEventWithUser(
      event.eventName,
      event.metadata,
      user,
      event.useServices
    );
  },
};

// Media service - upload files to S3
interface MediaUploadConfig {
  onMedia: (mediaFiles: Array<{ url: string; thumbnailUrl: string }>) => void;
  imageUploadFailure: (err: Error) => void;
  progress?: (event: { loaded: number; total: number }) => void;
  isStory?: boolean;
}

export const mediaService = {
  async upload(files: File[], config: MediaUploadConfig) {
    try {
      const mediaFiles: Array<{ url: string; thumbnailUrl: string }> = [];

      for (const file of files) {
        const url = await uploadFileToS3(
          { blob: file, fileName: file.name },
          {
            onProgress: (progress) => {
              if (config.progress) {
                config.progress({ loaded: progress, total: 100 });
              }
            },
          }
        );

        // For images, the thumbnail is the same as the URL
        // For videos, we'd need transcoding but this service is primarily used for images
        mediaFiles.push({
          url,
          thumbnailUrl: url,
        });
      }

      config.onMedia(mediaFiles);
    } catch (err) {
      config.imageUploadFailure(err as Error);
    }
  },
};

// Navigation service (use React Router instead)
export const navigationService = {
  go: (state: string, params?: any) => {
    console.warn(
      "navigationService.go called - use React Router instead",
      state,
      params
    );
    return Promise.resolve();
  },
};

// Global state accessors (prefer using Redux selectors directly)
export const globalState = {
  get user() {
    return getUser();
  },
  get accounts() {
    return getAccounts();
  },
  get accountsMap() {
    return getAccountsMap();
  },
};

/**
 * Legacy services bundle
 * @deprecated Use individual service imports instead
 */
export const legacyServices = {
  // Global state (prefer Redux selectors)
  $rootScope: {
    get user() {
      return getUser();
    },
    go(state: object) {
      return Promise.resolve(state);
    },
    openModal: (data: { templateUrl: string; backdrop?: string }) => {
      console.warn("openModal called - use React modal instead", data);
    },
    closeModal: () => {
      console.warn("closeModal called - use React modal state");
    },
    get accountsMap() {
      return getAccountsMap();
    },
    get accounts() {
      return getAccounts();
    },
  },
  $http: httpClient,
  $state: navigationService,
  toaster,
  loadingSpinnerService,
  eventService,
  mediaService,
  scheduleService,
  postService,
  postsService,
  collaborationService,
  postsController,
  storage,
  modalService,
} as ILegacyServices;

/**
 * @deprecated Use legacyServices instead
 */
export const ngDeps = legacyServices;

export function injectLegacyServices(deps: object) {
  Object.assign(legacyServices, deps);
}

/**
 * @deprecated Use injectLegacyServices instead
 */
export const injectNgDeps = injectLegacyServices;

export function getLegacyService(dep: keyof ILegacyServices) {
  if (!legacyServices[dep]) {
    return null;
  }
  return legacyServices[dep];
}

/**
 * @deprecated Use getLegacyService instead
 */
export const getNgDep = getLegacyService;

export default legacyServices;

// Type definitions

export interface ILegacyServices {
  $rootScope: {
    user: {
      timezone?: string;
      [key: string]: any;
    };
    go: (state: object) => Promise<object>;
    shuffleQueuedPosts?: (accountIds: string[], form: unknown) => void;
    openModal: (data: { templateUrl: string; backdrop?: string }) => void;
    closeModal: () => void;
    accountsMap: Record<string, AccountType>;
    accounts: AccountType[];
  };
  scheduleService: IScheduleService;
  postService: IPostService;
  postsService: IPostsService;
  $http: {
    get: (url: string, config?: any) => Promise<any>;
    post: (url: string, data?: any, config?: any) => Promise<any>;
    put: (url: string, data?: any, config?: any) => Promise<any>;
    delete: (url: string, config?: any) => Promise<any>;
  };
  collaborationService: ICollaborationService;
  toaster: {
    success: (opts: { title: string }) => void;
    error: (opts: { title: string }) => void;
    clear: () => void;
  };
  loadingSpinnerService: {
    showLoading: () => void;
    hideLoading: () => void;
  };
  eventService: {
    trackEvent: (event: LegacyTrackEvent) => void;
  };
  mediaService: IMediaService;
  $state: {
    go: (state: string, params?: any) => Promise<any>;
  };
  postsController: IPostsController;
  storage: IStorageService;
  modalService: IModalService;
}

/** @deprecated Use ILegacyServices instead */
export type INgDeps = ILegacyServices;

export interface IScheduleService {
  findPosts(params: any): Promise<any>;
  findCalendarPosts(params: any): any;
  findLatestPosts(params: any): Promise<any>;
  findArchives(params: any): Promise<any>;
  findActive(page: number, pageSize: number, accountIds: any[]): Promise<any>;
  findArchived(
    page: number,
    pageSize: number,
    accountIds: any[],
    params?: any
  ): Promise<any>;
  findDrafts(
    page: number,
    pageSize: number,
    accountIds: any[],
    params?: any
  ): Promise<any>;
  searchDrafts(accountIds: any[], searchText: string): Promise<any>;
  findActiveByTime(accountIds: any[]): Promise<any>;
  makeSchedule(post: any): any;
  normaliseComments(post: any): any;
  normaliseTags(tags: any): any[];
  update(id: string, schedule: any): Promise<any>;
  savePost(post: any): Promise<any>;
  clearSchedule(accountId: string): Promise<any>;
  copySchedule(sourceId: string, destinationId: string): Promise<any>;
  moveToQueue(post: any): Promise<any>;
  moveToSchedule(post: any): Promise<any>;
  updatePost(post: any): Promise<any>;
  updateRedirectUrl(post: any): Promise<any>;
  updateCalendarSchedule(
    id: string,
    schedule: any,
    hideCalendarDialog: boolean
  ): Promise<any>;
  moveCalendarPost(id: string, post: any, data: any): Promise<any>;
  schedulesOverlap(schedules: any[]): [number, number] | undefined;
  create(
    posts: any,
    uploader: any,
    suppress?: boolean,
    scope?: ScheduleSubmitScope
  ): Promise<any>;
  cancel(id: string): Promise<any>;
  findById(id: string): Promise<any>;
  remove(id: string): Promise<any>;
  removeFromQueue(id: string): Promise<any>;
  moveToDraft(id: string, queued: boolean): Promise<any>;
}

export interface IPostServiceEntity {
  update(post: any): Promise<any>;
  draft?(post: any): Promise<any>;
  queue?(post: any): Promise<any>;
  schedule?(post: any, scope?: ScheduleSubmitScope): Promise<any>;
}

export interface IPostService {
  getNext15Min(): any;
  draft: IPostServiceEntity;
  queue: IPostServiceEntity;
  schedule: IPostServiceEntity;
}

export interface IPostsService {
  makeFilters(accountIds: string[], limit?: number): any;
}

export interface ICollaborationService {
  updatePostStatusKey(
    postIds: string[],
    postType: string,
    key: string
  ): Promise<void>;
}

export interface IPostsController {
  currentPostUrl?: string;
  post?: any;
  getSelectedAccounts(): string[];
  toggleCollaborateWidget(index: number, post: any): void;
  closeModal(): void;
}

export interface IStorageService {
  getObject(key: string): any;
  putObject(key: string, value: any): void;
  remove(key: string): void;
}

export interface IModalService {
  open(config: any): {
    result: Promise<any>;
    close: () => void;
    dismiss: () => void;
  };
  createPostModal(config: any, callback?: () => void): void;
  close(): void;
}

export interface IMediaService {
  upload(
    files: File[],
    config: {
      onMedia: (
        mediaFiles: Array<{ url: string; thumbnailUrl: string }>
      ) => void;
      imageUploadFailure: (err: Error) => void;
      progress?: (event: { loaded: number; total: number }) => void;
      isStory?: boolean;
    }
  ): Promise<void>;
}
